import React, { useMemo, useState, useEffect, useCallback, useRef } from 'react';
import { useCRM } from '../utils/CRMContext';
import { AppHeader } from '../components/ui/AppHeader';
import { useNavigate } from 'react-router-dom';
import HubQuickSearch from '../components/search/HubQuickSearch';
import { useHubQuickSearchHotkey } from '../hooks/useHubQuickSearchHotkey';
import { searchWonDealsHub } from '../utils/hubSearch/searchSecondaryHubs';
import { HubSearchResult } from '../types/hubSearch';
import { Trophy, Calendar, DollarSign, User, MapPin, FileText, X, ClipboardList, PenTool, Pencil, Trash2, Receipt, ExternalLink } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { collection, query, onSnapshot, orderBy, doc, updateDoc, getDoc, Timestamp } from 'firebase/firestore';
import { db } from '../utils/firebase';
import { SalesOrder } from '../types';
import { getProjectDisplayName } from '../utils/projectName';
import { progressBillingInvoiceRows } from '../utils/paymentSchedule';
import { ensureBaseProposalSalesOrder } from '../utils/signedDealSync';
import { crmAuthedFetch } from '../utils/crmAuthedFetch';
import './WonDealsPage.css';

const WonDealsPage: React.FC = () => {
    const { enhancedDeals, leads, pipelines, deleteEnhancedDeal } = useCRM();
    const navigate = useNavigate();
    const [salesOrders, setSalesOrders] = useState<SalesOrder[]>([]);
    const [showQuickSearch, setShowQuickSearch] = useState(false);
    const [selectedOrder, setSelectedOrder] = useState<SalesOrder | null>(null);
    const [showModal, setShowModal] = useState(false);
    const [editingDateDealId, setEditingDateDealId] = useState<string | null>(null);
    const [creatingInvoice, setCreatingInvoice] = useState<string | null>(null);
    const [editingDateValue, setEditingDateValue] = useState<string>('');
    const [qbRealmId, setQbRealmId] = useState<string>('');
    const [qbEnv, setQbEnv] = useState<string>('production');

    // Load QB settings for invoice links
    useEffect(() => {
        const loadQbSettings = async () => {
            try {
                const settingsDoc = await getDoc(doc(db, 'settings', 'app_settings'));
                if (settingsDoc.exists()) {
                    const data = settingsDoc.data();
                    setQbRealmId(data.quickbooksRealmId || '');
                    setQbEnv(data.quickbooksEnvironment || 'production');
                }
            } catch (err) {
                console.error('Error loading QB settings:', err);
            }
        };
        loadQbSettings();
    }, []);

    const getQbInvoiceUrl = useCallback((invoiceId: string) => {
        if (!qbRealmId || !invoiceId) return '';
        const base = qbEnv === 'sandbox'
            ? 'https://app.sandbox.qbo.intuit.com'
            : 'https://app.qbo.intuit.com';
        return `${base}/app/invoice?txnId=${invoiceId}`;
    }, [qbRealmId, qbEnv]);

    // Subscribe to sales orders
    useEffect(() => {
        const q = query(
            collection(db, 'salesOrders'),
            orderBy('createdAt', 'desc')
        );

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const orders: SalesOrder[] = [];
            snapshot.forEach((doc) => {
                const data = doc.data();
                orders.push({
                    id: doc.id,
                    ...data,
                    signedAt: data.signedAt?.toDate?.() || new Date(data.signedAt),
                    createdAt: data.createdAt?.toDate?.() || new Date(data.createdAt),
                } as SalesOrder);
            });
            setSalesOrders(orders);
        });

        return () => unsubscribe();
    }, []);

    // Get all won deals
    const wonDeals = useMemo(() => {
        return enhancedDeals
            .filter(d => d.outcome === 'won')
            .sort((a, b) => {
                const dateA = a.closedAt ? new Date(a.closedAt).getTime() : 0;
                const dateB = b.closedAt ? new Date(b.closedAt).getTime() : 0;
                return dateB - dateA; // Most recent first
            });
    }, [enhancedDeals]);

    useHubQuickSearchHotkey({
        onOpen: () => setShowQuickSearch(true),
        blocked: showQuickSearch || showModal,
    });

    const getWonDealsQuickSearchResults = useCallback(
        (query: string) => searchWonDealsHub(query, enhancedDeals),
        [enhancedDeals],
    );

    const handleWonDealsQuickSearchSelect = useCallback(
        (result: HubSearchResult) => {
            const deal = result.payload as { leadId?: string };
            if (deal.leadId) navigate(`/client/${deal.leadId}`);
        },
        [navigate],
    );

    // Map deal IDs to sales orders. Base (proposal) sales orders drive the primary
    // invoice button; change-order sales orders are tracked separately so they don't
    // hijack the base order or its QuickBooks invoice linkage.
    const { salesOrderByDealId, changeOrderSalesOrdersByDealId } = useMemo(() => {
        const base = new Map<string, SalesOrder>();
        const co = new Map<string, SalesOrder[]>();
        // salesOrders is ordered desc by createdAt — first base order seen is newest.
        salesOrders.forEach(order => {
            if (!order.dealId) return;
            if (order.isChangeOrder) {
                const arr = co.get(order.dealId) || [];
                arr.push(order);
                co.set(order.dealId, arr);
            } else if (!base.has(order.dealId)) {
                base.set(order.dealId, order);
            }
        });
        return { salesOrderByDealId: base, changeOrderSalesOrdersByDealId: co };
    }, [salesOrders]);

    // Backfill missing base (proposal) sales orders when only change-order orders exist.
    const baseBackfillRef = useRef<Set<string>>(new Set());
    useEffect(() => {
        wonDeals.forEach(deal => {
            if (!deal.id || salesOrderByDealId.has(deal.id)) return;
            if (baseBackfillRef.current.has(deal.id)) return;
            baseBackfillRef.current.add(deal.id);
            ensureBaseProposalSalesOrder(deal.id).catch(err => {
                baseBackfillRef.current.delete(deal.id!);
                console.warn('[WonDeals] base sales order backfill failed', err);
            });
        });
    }, [wonDeals, salesOrderByDealId]);

    // Calculate totals
    const stats = useMemo(() => {
        const total = wonDeals.reduce((sum, d) => sum + (d.value || 0), 0);
        const count = wonDeals.length;
        const avgValue = count > 0 ? total / count : 0;
        return { total, count, avgValue };
    }, [wonDeals]);

    // Get pipeline name for a deal
    const getPipelineName = (pipelineId: string) => {
        const pipeline = pipelines.find(p => p.id === pipelineId);
        return pipeline?.name || 'Unknown Pipeline';
    };

    // Get lead info for a deal
    const getLeadInfo = (leadId?: string) => {
        if (!leadId) return null;
        return leads.find(l => l.id === leadId);
    };

    const formatCurrency = (value: number): string => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        }).format(value);
    };

    const handleViewSalesOrder = (order: SalesOrder) => {
        setSelectedOrder(order);
        setShowModal(true);
    };

    const startEditingDate = useCallback((dealId: string, currentDate: Date | null) => {
        setEditingDateDealId(dealId);
        setEditingDateValue(currentDate ? currentDate.toISOString().split('T')[0] : '');
    }, []);

    const saveDate = useCallback(async (dealId: string, value: string) => {
        if (!value) {
            console.log('[WonDeals] saveDate: no value, skipping');
            return;
        }
        console.log('[WonDeals] saveDate called:', { dealId, value });
        try {
            const dateObj = new Date(value + 'T12:00:00');
            const timestamp = Timestamp.fromDate(dateObj);
            console.log('[WonDeals] Writing to Firestore:', { dealId, dateObj: dateObj.toISOString(), timestamp });
            const docRef = doc(db, 'enhancedDeals', dealId);
            await updateDoc(docRef, {
                closedAt: timestamp,
                updatedAt: Timestamp.fromDate(new Date()),
            });
            console.log('[WonDeals] Firestore write successful');
        } catch (err) {
            console.error('[WonDeals] Error updating closedAt:', err);
        }
        setEditingDateDealId(null);
    }, []);

    const handleDeleteDeal = useCallback(async (dealId: string, dealTitle: string) => {
        if (!window.confirm(`Delete "${dealTitle}"? This cannot be undone.`)) return;
        try {
            await deleteEnhancedDeal(dealId);
        } catch (err) {
            console.error('Error deleting deal:', err);
            alert('Failed to delete deal. Please try again.');
        }
    }, [deleteEnhancedDeal]);

    const handleCreateInvoice = useCallback(async (salesOrderId: string, force = false) => {
        setCreatingInvoice(salesOrderId);
        try {
            const resp = await crmAuthedFetch('https://us-central1-lctf-projects.cloudfunctions.net/createQuickBooksInvoice', {
                method: 'POST',
                body: JSON.stringify({ salesOrderId, force }),
            });
            const data = await resp.json();
            if (data.success) {
                alert(`Invoice #${data.invoiceNumber} created for ${data.customerName} ($${data.totalAmount?.toLocaleString()})`);
            } else if (resp.status === 409 && !force) {
                if (window.confirm('An invoice was previously linked to this order. Recreate a new invoice?')) {
                    setCreatingInvoice(null);
                    handleCreateInvoice(salesOrderId, true);
                    return;
                }
            } else {
                alert(`Invoice creation failed: ${data.error}`);
            }
        } catch (err) {
            console.error('Error creating QB invoice:', err);
            alert('Failed to create invoice');
        } finally {
            setCreatingInvoice(null);
        }
    }, []);

    const closeModal = () => {
        setShowModal(false);
        setSelectedOrder(null);
    };

    return (
        <div className="won-deals-page">
            <AppHeader />
            <div className="won-deals-content">
                <div className="won-deals-header">
                    <div className="header-title">
                        <Trophy size={28} className="trophy-icon" />
                        <h1>Won Deals</h1>
                    </div>
                    <p className="header-subtitle">Closed deals and revenue tracking</p>
                </div>

                {/* Stats Cards */}
                <div className="stats-grid">
                    <div className="stat-card">
                        <div className="stat-icon won">
                            <Trophy size={24} />
                        </div>
                        <div className="stat-info">
                            <span className="stat-value">{stats.count}</span>
                            <span className="stat-label">Total Deals Won</span>
                        </div>
                    </div>
                    <div className="stat-card">
                        <div className="stat-icon revenue">
                            <DollarSign size={24} />
                        </div>
                        <div className="stat-info">
                            <span className="stat-value">{formatCurrency(stats.total)}</span>
                            <span className="stat-label">Total Revenue</span>
                        </div>
                    </div>
                    <div className="stat-card">
                        <div className="stat-icon avg">
                            <FileText size={24} />
                        </div>
                        <div className="stat-info">
                            <span className="stat-value">{formatCurrency(stats.avgValue)}</span>
                            <span className="stat-label">Average Deal Value</span>
                        </div>
                    </div>
                </div>

                {/* Won Deals List */}
                <div className="deals-list-container">
                    <div className="deals-list-header">
                        <h2>Closed Deals</h2>
                    </div>

                    {wonDeals.length === 0 ? (
                        <div className="empty-state">
                            <Trophy size={48} />
                            <h3>No Won Deals Yet</h3>
                            <p>When deals are marked as won, they'll appear here.</p>
                        </div>
                    ) : (
                        <div className="deals-table">
                            <div className="table-header">
                                <div className="col-deal">Deal</div>
                                <div className="col-client">Client</div>
                                <div className="col-pipeline">Pipeline</div>
                                <div className="col-value">Value</div>
                                <div className="col-date">Closed Date</div>
                                <div className="col-actions">Sales Order / Invoice</div>
                                <div className="col-delete"></div>
                            </div>
                            {wonDeals.map(deal => {
                                const lead = getLeadInfo(deal.leadId);
                                const closeDate = deal.closedAt
                                    ? new Date(deal.closedAt)
                                    : null;
                                const salesOrder = deal.id ? salesOrderByDealId.get(deal.id) : null;
                                const changeOrderSos = deal.id ? changeOrderSalesOrdersByDealId.get(deal.id) || [] : [];
                                const changeOrderTotal = changeOrderSos.reduce((s, o) => s + (o.grandTotal || 0), 0);
                                const leadName = lead
                                    ? `${lead.firstName || ''} ${lead.lastName || ''}`.trim()
                                    : '';
                                const clientName = leadName || deal.contactName || 'Unknown';
                                const clientCompany = (lead?.company || deal.company || '').trim();
                                const progressRows = salesOrder?.billingStyle === 'progress'
                                    ? progressBillingInvoiceRows(salesOrder)
                                    : [];

                                return (
                                    <div key={deal.id} className="table-row">
                                        <div className="col-deal">
                                            <div className="deal-title">{getProjectDisplayName(deal)}</div>
                                        </div>
                                        <div className="col-client">
                                            <div className="client-info">
                                                <User size={14} />
                                                <span>{clientName}</span>
                                            </div>
                                            {clientCompany && (
                                                <div className="client-location">
                                                    <span>{clientCompany}</span>
                                                </div>
                                            )}
                                            {(lead?.location || deal.location) && (
                                                <div className="client-location">
                                                    <MapPin size={12} />
                                                    <span>{lead?.location || deal.location}</span>
                                                </div>
                                            )}
                                        </div>
                                        <div className="col-pipeline">
                                            <span className="pipeline-badge">
                                                {getPipelineName(deal.pipelineId)}
                                            </span>
                                        </div>
                                        <div className="col-value">
                                            <span className="value-amount">{formatCurrency(deal.value)}</span>
                                            {changeOrderSos.length > 0 && (
                                                <span
                                                    className="value-change-orders"
                                                    title={`Includes ${changeOrderSos.length} approved change order${changeOrderSos.length > 1 ? 's' : ''}`}
                                                    style={{ display: 'block', fontSize: 11, color: changeOrderTotal < 0 ? '#b4453a' : '#2c6b3c', marginTop: 2 }}
                                                >
                                                    {changeOrderTotal >= 0 ? '+' : ''}{formatCurrency(changeOrderTotal)} · {changeOrderSos.length} CO{changeOrderSos.length > 1 ? 's' : ''}
                                                </span>
                                            )}
                                        </div>
                                        <div className="col-date">
                                            {editingDateDealId === deal.id ? (
                                                <div className="date-edit-group">
                                                    <input
                                                        type="date"
                                                        className="date-edit-input"
                                                        value={editingDateValue}
                                                        autoFocus
                                                        onChange={(e) => setEditingDateValue(e.target.value)}
                                                        onKeyDown={(e) => {
                                                            if (e.key === 'Enter' && deal.id && editingDateValue) {
                                                                saveDate(deal.id, editingDateValue);
                                                            } else if (e.key === 'Escape') {
                                                                setEditingDateDealId(null);
                                                            }
                                                        }}
                                                    />
                                                    <div className="date-edit-actions">
                                                        <button
                                                            className="date-save-btn"
                                                            onClick={() => deal.id && editingDateValue && saveDate(deal.id, editingDateValue)}
                                                            disabled={!editingDateValue}
                                                        >
                                                            Save
                                                        </button>
                                                        <button
                                                            className="date-cancel-btn"
                                                            onClick={() => setEditingDateDealId(null)}
                                                        >
                                                            Cancel
                                                        </button>
                                                    </div>
                                                </div>
                                            ) : closeDate ? (
                                                <div
                                                    className="date-editable"
                                                    onClick={() => deal.id && startEditingDate(deal.id, closeDate)}
                                                    title="Click to edit closed date"
                                                >
                                                    <div className="date-primary">
                                                        <Calendar size={14} />
                                                        {closeDate.toLocaleDateString('en-US', {
                                                            month: 'short',
                                                            day: 'numeric',
                                                            year: 'numeric'
                                                        })}
                                                        <Pencil size={12} className="date-edit-icon" />
                                                    </div>
                                                    <div className="date-relative">
                                                        {formatDistanceToNow(closeDate, { addSuffix: true })}
                                                    </div>
                                                </div>
                                            ) : (
                                                <span
                                                    className="no-date clickable"
                                                    onClick={() => deal.id && startEditingDate(deal.id, null)}
                                                    title="Click to set closed date"
                                                >
                                                    Set date
                                                </span>
                                            )}
                                        </div>
                                        <div className="col-actions">
                                            <div style={{ display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap' }}>
                                                {salesOrder && (
                                                    <button
                                                        className="view-order-btn"
                                                        onClick={() => handleViewSalesOrder(salesOrder)}
                                                        title="View Sales Order"
                                                    >
                                                        <ClipboardList size={16} />
                                                        <span>Order</span>
                                                    </button>
                                                )}
                                                {progressRows.length > 0 ? (
                                                    <div className="progress-billing-cell">
                                                        <span className="progress-billing-badge" title="50% at signing, 40% before shipment, 10% at delivery">
                                                            Progress billing
                                                        </span>
                                                        {progressRows.map((row) => {
                                                            const url = row.invoiceId ? getQbInvoiceUrl(row.invoiceId) : '';
                                                            const chip = (
                                                                <>
                                                                    <Receipt size={12} />
                                                                    <span>{row.label}</span>
                                                                    {row.invoiceNum ? <span>#{row.invoiceNum}</span> : <span className="progress-not-billed">Not invoiced</span>}
                                                                    {url ? <ExternalLink size={10} /> : null}
                                                                </>
                                                            );
                                                            return url ? (
                                                                <a
                                                                    key={row.id}
                                                                    href={url}
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                    className="qb-invoice-badge qb-invoice-link progress-installment"
                                                                    title={`${row.detail} · ${formatCurrency(row.amount)}`}
                                                                >
                                                                    {chip}
                                                                </a>
                                                            ) : (
                                                                <span
                                                                    key={row.id}
                                                                    className="qb-invoice-badge progress-installment progress-installment-pending"
                                                                    title={`${row.detail} · ${formatCurrency(row.amount)}`}
                                                                >
                                                                    {chip}
                                                                </span>
                                                            );
                                                        })}
                                                    </div>
                                                ) : salesOrder?.quickbooksInvoiceNum || deal.quickbooksInvoiceNum ? (
                                                    (() => {
                                                        const invoiceId = salesOrder?.quickbooksInvoiceId || deal.quickbooksInvoiceId;
                                                        const invoiceNum = salesOrder?.quickbooksInvoiceNum || deal.quickbooksInvoiceNum;
                                                        const url = invoiceId ? getQbInvoiceUrl(invoiceId) : '';
                                                        return url ? (
                                                            <a
                                                                href={url}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="qb-invoice-badge qb-invoice-link"
                                                                title={`Open QB Invoice #${invoiceNum}`}
                                                            >
                                                                <Receipt size={13} />
                                                                #{invoiceNum}
                                                                <ExternalLink size={11} />
                                                            </a>
                                                        ) : (
                                                            <span className="qb-invoice-badge" title={`QB Invoice #${invoiceNum}`}>
                                                                <Receipt size={13} />
                                                                #{invoiceNum}
                                                            </span>
                                                        );
                                                    })()
                                                ) : salesOrder ? (
                                                    <button
                                                        className="create-invoice-btn"
                                                        onClick={() => salesOrder.id && handleCreateInvoice(salesOrder.id)}
                                                        disabled={creatingInvoice === salesOrder.id}
                                                        title="Create QuickBooks Invoice"
                                                    >
                                                        <Receipt size={13} />
                                                        {creatingInvoice === salesOrder.id ? '...' : 'Invoice'}
                                                    </button>
                                                ) : (
                                                    <span className="no-order">—</span>
                                                )}
                                            </div>
                                        </div>
                                        <div className="col-delete">
                                            <button
                                                className="delete-deal-btn"
                                                onClick={() => deal.id && handleDeleteDeal(deal.id, deal.title)}
                                                title="Delete deal"
                                            >
                                                <Trash2 size={15} />
                                            </button>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            </div>

            {/* Sales Order Modal */}
            {showModal && selectedOrder && (
                <div className="modal-overlay" onClick={closeModal}>
                    <div className="modal-content sales-order-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h2>
                                <ClipboardList size={24} />
                                Sales Order
                            </h2>
                            <button className="modal-close" onClick={closeModal}>
                                <X size={24} />
                            </button>
                        </div>
                        <div className="modal-body">
                            {/* Order Summary */}
                            <div className="order-summary">
                                <div className="summary-row">
                                    <span className="label">Client:</span>
                                    <span className="value">{(() => {
                                        const deal = enhancedDeals.find(d => d.id === selectedOrder.dealId);
                                        const lead = getLeadInfo(deal?.leadId);
                                        const name = lead
                                            ? `${lead.firstName || ''} ${lead.lastName || ''}`.trim()
                                            : (deal?.contactName || selectedOrder.clientName);
                                        const company = (lead?.company || deal?.company || '').trim();
                                        return company ? `${name} · ${company}` : name;
                                    })()}</span>
                                </div>
                                <div className="summary-row">
                                    <span className="label">Signed By:</span>
                                    <span className="value">{selectedOrder.signedBy}</span>
                                </div>
                                <div className="summary-row">
                                    <span className="label">Signed Date:</span>
                                    <span className="value">
                                        {new Date(selectedOrder.signedAt).toLocaleDateString('en-US', {
                                            weekday: 'long',
                                            year: 'numeric',
                                            month: 'long',
                                            day: 'numeric',
                                        })}
                                    </span>
                                </div>
                                <div className="summary-row">
                                    <span className="label">Installation:</span>
                                    <span className={`value ${selectedOrder.includeInstall ? 'included' : 'not-included'}`}>
                                        {selectedOrder.includeInstall ? 'Included' : 'Not Included'}
                                    </span>
                                </div>
                            </div>

                            {selectedOrder.billingStyle === 'progress' && (
                                <div className="order-progress">
                                    <h3>Progress billing</h3>
                                    <p className="order-progress-note">
                                        This contract is billed in three parts: 50% when it is signed, 40% before shipment, and 10% at delivery. The deposit is one invoice for the whole contract. The 40% and 10% can be invoiced for selected structure groups, and any amount not yet invoiced stays on the remaining row.
                                    </p>
                                    <table className="line-items-table">
                                        <thead>
                                            <tr>
                                                <th>Installment</th>
                                                <th>When</th>
                                                <th>Invoice</th>
                                                <th>Amount</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {progressBillingInvoiceRows(selectedOrder).map((row) => {
                                                const url = row.invoiceId ? getQbInvoiceUrl(row.invoiceId) : '';
                                                return (
                                                    <tr key={row.id}>
                                                        <td>{row.label}</td>
                                                        <td>{row.detail}</td>
                                                        <td>
                                                            {row.invoiceNum && url ? (
                                                                <a href={url} target="_blank" rel="noopener noreferrer">
                                                                    #{row.invoiceNum}
                                                                </a>
                                                            ) : row.invoiceNum ? (
                                                                `#${row.invoiceNum}`
                                                            ) : (
                                                                'Not invoiced yet'
                                                            )}
                                                        </td>
                                                        <td>{formatCurrency(row.amount)}</td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            )}

                            {/* Line Items */}
                            <div className="order-line-items">
                                <h3>Line Items</h3>
                                <table className="line-items-table">
                                    <thead>
                                        <tr>
                                            <th>Structure</th>
                                            <th>Species</th>
                                            <th>Qty</th>
                                            <th>Design</th>
                                            <th>Timber Kit</th>
                                            <th>Install</th>
                                            <th>Subtotal</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {selectedOrder.lineItems.map((item, idx) => (
                                            <tr key={idx}>
                                                <td>{item.structureName}</td>
                                                <td>{item.species}</td>
                                                <td>{item.quantity}</td>
                                                <td>{formatCurrency(item.designCost)}</td>
                                                <td>{formatCurrency(item.timberKitCost)}</td>
                                                <td>{formatCurrency(item.installCost)}</td>
                                                <td className="subtotal">{formatCurrency(item.subtotal)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            {/* Totals */}
                            <div className="order-totals">
                                <div className="total-row">
                                    <span>Design Total:</span>
                                    <span>{formatCurrency(selectedOrder.designTotal)}</span>
                                </div>
                                <div className="total-row">
                                    <span>Timber Kit Total:</span>
                                    <span>{formatCurrency(selectedOrder.timberKitTotal)}</span>
                                </div>
                                {selectedOrder.includeInstall && (
                                    <div className="total-row">
                                        <span>Installation Total:</span>
                                        <span>{formatCurrency(selectedOrder.installTotal)}</span>
                                    </div>
                                )}
                                <div className="total-row grand-total">
                                    <span>Grand Total:</span>
                                    <span>{formatCurrency(selectedOrder.grandTotal)}</span>
                                </div>
                            </div>

                            {/* Signature */}
                            {selectedOrder.signatureDataUrl && (
                                <div className="order-signature">
                                    <h3>
                                        <PenTool size={18} />
                                        Signature
                                    </h3>
                                    <div className="signature-image">
                                        <img src={selectedOrder.signatureDataUrl} alt="Client Signature" />
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
            <HubQuickSearch
                open={showQuickSearch}
                onClose={() => setShowQuickSearch(false)}
                getResults={getWonDealsQuickSearchResults}
                onSelect={handleWonDealsQuickSearchSelect}
                placeholder="Search won deals…"
                ariaLabel="Won deals quick search"
                renderIcon={() => <Trophy size={16} />}
            />
        </div>
    );
};

export default WonDealsPage;
